import React, { useState } from "react";
import { Column, Row, Text, Card, Divider, Expander, Icon, Button, Loader } from "@amzn/meridian";
import { InspectionResult } from "src/services/inspectionService";
import ThemedTag from "./ThemedTag";
import ComplianceResultRow from "./ComplianceResultRow";
import ComplianceDetailSheet from "./ComplianceDetailSheet";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import "./ReviewCheckpointCard.scss";

export interface ReviewCheckpointCardProps {
    questionNumber: string;
    questionText: string;
    isAI: boolean;
    allCompliant: boolean;
    isNA?: boolean;
    isAnalysisPending?: boolean;
    results: InspectionResult[];
    presignedUrlMap: Record<string, string>;
    onOverride: (result: InspectionResult) => void;
}

const ReviewCheckpointCard: React.FC<ReviewCheckpointCardProps> = ({
    questionNumber,
    questionText,
    isAI,
    results,
    allCompliant,
    isNA = false,
    isAnalysisPending = false,
    presignedUrlMap,
    onOverride,
}) => {
    const [showDetails, setShowDetails] = useState(false);
    const [selectedResult, setSelectedResult] = useState<InspectionResult | null>(null);
    const [sheetOpen, setSheetOpen] = useState(false);

    const openSheet = (result: InspectionResult) => {
        setSelectedResult(result);
        setSheetOpen(true);
    };

    const handleOverride = (result: InspectionResult) => {
        setSheetOpen(false);
        onOverride(result);
    };

    return (
        <Card spacingInset="none">
            <Column spacingInset="400" spacing="300">
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Text type="h100" color="primary">
                        {questionNumber}
                    </Text>
                    <Row spacing="200" alignmentVertical="center">
                        {isAnalysisPending ? (
                            <Row spacing="200" alignmentVertical="center">
                                <Text type="b100" color="secondary">Analyzing...</Text>
                                <Loader size="small" type="circular" />
                            </Row>
                        ) : isNA ? (
                            <ThemedTag text="N/A" type="gray" />
                        ) : allCompliant ? (
                            <ThemedTag text="Compliant" type="success" />
                        ) : (
                            <ThemedTag text={"Non Compliant"} type="error" />
                        )}

                        {/* Show AI/Manual source tag only after analysis is complete */}
                        {!isAnalysisPending && <ThemedTag text={isAI ? "AI" : "Manual"} type="neutral" />}
                    </Row>
                </Row>

                <Text type="b200" color="secondary">
                    {questionText}
                </Text>

                <Divider />

                {results[0].objectId === null ?
                    <Row alignmentHorizontal="justify" alignmentVertical="center" spacingInset="200 none">
                        <Text type="b200" color="secondary">{results[0].confirmedResult}</Text>
                        {results[0].evidence.length > 0 && (
                            <Button onClick={() => openSheet(results[0])} type="icon">
                                <Icon tokens={chevronRightSmallTokens} color="secondary" />
                            </Button>
                        )}
                    </Row> :

                    <Expander
                        title="Detailed compliance status"
                        open={showDetails}
                        onChange={(open: boolean) => setShowDetails(open)}
                        type="inline"
                        spacingInset="none"
                    >
                        <Column spacing="none">
                            {results.map((result, idx) => (
                                <ComplianceResultRow
                                    key={result.resultId}
                                    result={result}
                                    index={idx}
                                    onClick={() => openSheet(result)}
                                    isLast={idx === results.length - 1}
                                />
                            ))}
                        </Column>
                    </Expander>}
            </Column>

            <ComplianceDetailSheet
                open={sheetOpen}
                onClose={() => setSheetOpen(false)}
                result={selectedResult}
                presignedUrlMap={presignedUrlMap}
                onOverride={handleOverride}
            />
        </Card>
    );
};

export default ReviewCheckpointCard;
